import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { Guide } from "types";
import { api } from "../../../../api/api";
import { useSeller } from "../../../../hooks/useAccount";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { useResource } from "../../../../hooks/useResource";
import { ConfirmLeaveModal } from "../../../Common/ConfirmLeaveModal";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { ContentLayout } from "../../../Layout/ContentLayout";

export const PreferencesTab = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [enabled, setEnabled] = useState(false);
    const [forceConfirm, setForceConfirm] = useState(false);
    const { id } = useParams<{ id?: ID }>();
    const { data: guide } = useResource<Guide>(["sellers", seller.id, "guides", id]);
    const initialValues = guide?.overlappingEvents ?? { enabled: false, forceConfirm: false };

    useEffect(() => {
        if (guide?.overlappingEvents) {
            setEnabled(guide.overlappingEvents.enabled);
            setForceConfirm(guide.overlappingEvents.forceConfirm);
        }
    }, [guide]);

    const { mutateAsync: updatePreferences, isLoading: isUpdating } = useMutation(async () => {
        await api.put(`/sellers/${seller.id}/guides/${guide?.id}`, {
            ...guide,
            overlappingEvents: { enabled, forceConfirm },
        });

        invalidate("guides", guide?.id);
        flash.success("Preference saved");
    });

    const handleCancelClick = async () => {
        if (guide) {
            setEnabled(!!guide.overlappingEvents?.enabled);
            setForceConfirm(!!guide.overlappingEvents?.forceConfirm);
        }
    };

    return (
        <ContentLayout className="space-y-4" isDisabled={isUpdating}>
            <ConfirmLeaveModal isFormTouched={!isEqual(initialValues, { enabled, forceConfirm })} />
            <Switch.Group>
                <Switch
                    data-testid="preferences-tab-overlapping-events-toggle"
                    isChecked={enabled}
                    size="medium"
                    onChange={(enabled: boolean) => {
                        setEnabled(enabled);
                    }}
                />

                <Switch.Label direction="right">
                    <h4 className="font-bold">Allow this guide to be assigned to overlapping events</h4>
                </Switch.Label>
            </Switch.Group>

            {enabled ? (
                <Switch.Group>
                    <Switch
                        data-testid="preferences-tab-double-confirmation-for-overlapping-events-toggle"
                        isChecked={forceConfirm}
                        size="medium"
                        onChange={(forceConfirm: boolean) => {
                            setForceConfirm(forceConfirm);
                        }}
                    />

                    <Switch.Label direction="right">
                        <h4 className="font-bold">Require double confirmation when assigning to overlapping events</h4>
                    </Switch.Label>
                </Switch.Group>
            ) : null}

            <ContentLayout.Footer>
                <SubmitButton isLoading={isUpdating} onClick={updatePreferences}>
                    Save
                </SubmitButton>

                <SecondaryButton type="reset" onClick={handleCancelClick}>
                    Cancel
                </SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};
